// FIX TS ANY - getDonorFormAttributes state needs more definition for proper handling beyond anys

import formIndexMap from '../Redux/Forms/FormIndexMap';
import type {
  Donor,
  DonorTask,
  StaticTask,
  FormValueMap,
  ReferenceWorkflow,
  ReferenceForm,
  Workflow,
  WorkflowAndForms,
  CaseFormDefMap,
  FormDefDataMap,
} from './types';

import type { ApiTask } from './apiTypes';

export type DonorFormAttributes = {
  donorId: number;
  donor: Donor;
  taskId: number;
  donorTask: DonorTask;
  formId: string;
  workflowId: string;
  staticTask: StaticTask;
  formData: FormValueMap;
};

const defaultEmptyObject: any = {};

// this reusable code navigates the complexities of forms and workflows to return a commonly used set of data from config
// If donor Id/task Id is provided (TaskItem) then it is used. Otherwise it is presumed the donor panel is open and uses donorTask from state
export const getDonorFormAttributes = (
  state: any,
  donorIdArg?: number,
  taskIdArg: number = -1,
): DonorFormAttributes => {
  let donorId: number;
  let taskId: number = -1;
  let donorTask = defaultEmptyObject;
  if (donorIdArg) {
    donorId = donorIdArg;
    taskId = taskIdArg;
  } else {
    donorTask = state.donor.donorTask ? state.donor.donorTask : defaultEmptyObject;
    ({ donorId, taskId } = donorTask);
  }
  const donor = (donorId && donorId !== -1 ? state.donor.donors[donorId] : defaultEmptyObject) || defaultEmptyObject;
  const workflowId = (donor ? donor.workflow : null) || 'LEGACY';

  const staticTasks: Array<StaticTask> =
    state.donor.workflowTasks && state.donor.workflowTasks[workflowId] && state.donor.workflowTasks[workflowId].tasks
      ? state.donor.workflowTasks[workflowId].tasks
      : [];

  let staticTask: StaticTask = defaultEmptyObject;
  if (staticTasks && staticTasks.length > 0) {
    staticTask = staticTasks.find((obj) => obj.taskId === taskId) || defaultEmptyObject;
  }

  let formId = '';
  if (staticTask && staticTask.details) {
    formId = staticTask.formId || formIndexMap[staticTask.details.description];
  }

  const taskData = donor && donor.taskData && donor.taskData[taskId] ? donor.taskData[taskId] : {};

  const attributes: DonorFormAttributes = {
    donorId,
    donor,
    taskId,
    donorTask,
    formId,
    workflowId,
    staticTask,
    formData: taskData,
  };

  return attributes;
};

/**
 * Transform a workflow object pulled from the database into a form more usable in the app
 */
export function transformWorkflowReference(workflowRef: ReferenceWorkflow): Workflow {
  const rd = workflowRef.reference_data;
  const { tasks, trackers } = rd;
  const sections: string[] = tasks
    .map((task) => (typeof task.section === 'string' ? task.section : undefined))
    .filter((section): section is string => section !== undefined);
  const sectionOrder = sections.length > 0 ? [...new Set(sections)] : [];

  return {
    key: workflowRef.key,
    name: rd.name || '',
    type: rd.type,
    ownerId: rd.ownerId,
    ownerType: rd.ownerType,
    organizationType: rd.organizationType,
    flat: rd.flat || false,
    progressBar: rd.progressBar || false,
    tasks: tasks.map((task) => ({
      taskId: task.id,
      formId: task.formId,
      details: {
        section: task.section || '',
        description: task.description,
        highRisk: task.highRisk ?? false,
      },
    })),
    sectionOrder,
    trackers: trackers.map((tracker) => ({
      id: tracker.id,
      title: tracker.title,
    })),
    lastModifiedDate: workflowRef.last_modified_date,
  };
}

/**
 * Transform an object contain a workflow and forms from the database
 * into a more appropriate form for the app
 */
export function transformWorkflowDef({
  workflow,
  forms,
}: {
  workflow: ReferenceWorkflow;
  forms: Array<ReferenceForm>;
}): WorkflowAndForms {
  return {
    workflow: transformWorkflowReference(workflow),
    forms: forms.map((f) => f.reference_data),
  };
}

export const transformFormData = (forms: ReferenceForm[]) =>
  forms.reduce((acc: Record<string, any>, form) => {
    const { key, reference_data: referenceData } = form;
    acc[key] = {
      ...referenceData,
      formId: key,
    };
    return acc;
  }, {});

export const transformApiTaskToStaticTask = (taskId: string, task: ApiTask): StaticTask => ({
  taskId: parseInt(taskId, 10),
  formId: task.form_id,
  details: {
    sectionPosition: task.section_position,
    section: task.section,
    description: task.description,
    highRisk: task.high_risk,
  },
});

/**
 * Organize case form definitions by their task id and field id for easy reference
 */
export const organizeCaseFormDefinitions = (
  workflowTaskDefs: StaticTask[],
  formDefs: FormDefDataMap,
): CaseFormDefMap => {
  const formToTaskIdMap: Record<string, number> = workflowTaskDefs.reduce(
    (acc, taskDef) => {
      if (taskDef.formId) {
        acc[taskDef.formId] = taskDef.taskId;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  return Object.values(formDefs).reduce((acc: CaseFormDefMap, curr) => {
    const formId = curr.formId;
    const taskId = formToTaskIdMap[formId];

    if (!taskId) {
      return acc;
    }

    curr.sections.forEach((form) => {
      if (!acc[taskId]) {
        acc[taskId] = {};
      }

      form.fields.forEach((field) => {
        acc[taskId][field.id] = field;
      });
    });
    return acc;
  }, {});
};
